import { FC, MouseEventHandler, useState, useMemo } from 'react';
import { useStore } from '../../../../hooks/useStore';
import IconLogoFlightDeck from '../../../icons/IconLogoFlightDeck';
import IconMenuToggle from '../../../icons/iconMenuToggle';
import IconSearch from '../../../icons/IconSearch';
import IconButton from '../../../ui/IconButton';
import UserProfileWidget from '../UserProfileWidget';
import {
  Button,
  CalendarIcon,
  MailIcon,
  IconButton as UnifiedIconButton,
  IconStarBadge,
  PresentationIcon,
  SearchInputExternalResolver
} from 'stubben-edge-unified-frontend-react-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import NotificationPanel from '../Header/components/NotificationPanel';
import { ShoppingCartIcon } from 'lucide-react';
import MobileSearchModal from '../Header/components/MobileSearchModal';
import { useGlobalSearch } from '../../../../hooks/useGlobalSearch';
import { transformSearchResultsToOptions } from '../../../../utils/searchUtils';
import { useMeetingsListQuery } from '@/api-client/meetings';
import {
  MeetingImportStates,
  MeetingListScopes
} from '@/components/page-components/meetings/types';
import { canAccessMeetings } from '@/utils/auth/canAccessMeetings';

const Navbar: FC<{ hideMenuButton?: boolean }> = ({
  hideMenuButton = false
}) => {
  const { interfaceStore, authStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const hasAiAssistantPermission = authStore.hasPermission(['chat_with_data']);
  const hasMeetingsAccess = canAccessMeetings(authStore);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [desktopSearchTerm, setDesktopSearchTerm] = useState('');
  const isMeetingsPage = location.pathname.startsWith('/meetings');

  const { data: reviewMeetingsResponse } = useMeetingsListQuery(
    {
      scope: MeetingListScopes.My,
      import_state: MeetingImportStates.ForImportReview,
      per_page: 1
    },
    {
      enabled: hasMeetingsAccess,
      staleTime: 60_000,
      refetchInterval: false
    }
  );

  /** Pending review count for Meetings header badge. */
  const meetingsNeedsReviewCount = useMemo(() => {
    if (!reviewMeetingsResponse) return 0;
    if (Array.isArray(reviewMeetingsResponse)) {
      return reviewMeetingsResponse.filter(
        (meeting) =>
          meeting.import_state === MeetingImportStates.ForImportReview
      ).length;
    }
    return reviewMeetingsResponse.meta?.total ?? 0;
  }, [reviewMeetingsResponse]);

  const { data: desktopSearchData, isLoading: isDesktopSearchLoading } =
    useGlobalSearch({
      searchTerm: desktopSearchTerm,
      enabled: true
    });

  const desktopSearchOptions = useMemo(() => {
    if (!desktopSearchData?.items) return [];
    return transformSearchResultsToOptions(desktopSearchData.items);
  }, [desktopSearchData]);

  const handleToggleMenu: MouseEventHandler = (e) => {
    interfaceStore.toggleMenuDrawer();
  };

  const handleOpenSearch: MouseEventHandler = (e) => {
    setIsSearchModalOpen(true);
  };

  const handleDesktopSearchSelect = (optionValue: string | number) => {
    const selectedOption = desktopSearchOptions.find(
      (opt) => opt.value === optionValue
    );
    if (selectedOption?.href) {
      // Extract pathname if it's an absolute URL, otherwise use as-is
      const url = selectedOption.href.startsWith('http')
        ? new URL(selectedOption.href).pathname
        : selectedOption.href;
      navigate(url);
      setDesktopSearchTerm('');
    }
  };

  return (
    <>
      {/* Mobile Navbar */}
      <div className="flex items-center justify-between bg-white px-3 py-2 lg:hidden">
        <div
          key="navigation-logo"
          onClick={() => navigate('/dashboard')}
          data-testid="navbar-mobile-dashboard-link"
        >
          <IconLogoFlightDeck />
        </div>
        <div className="inline-flex items-center gap-2">
          <IconButton
            color="primary"
            variant="text"
            onClick={handleOpenSearch}
            className={isSearchModalOpen ? 'border-2 border-[#C3DBFF]' : ''}
            data-testid="navbar-mobile-open-search"
          >
            <IconSearch />
          </IconButton>
          <UnifiedIconButton
            mode="ghost"
            onClick={() => navigate('/mail')}
            testIdentifier="navbar-mobile-mail"
          >
            <MailIcon size={24} />
          </UnifiedIconButton>
          <div className="relative mr-3 inline-block">
            <UserProfileWidget testIdentifierPrefix="navbar-mobile-user-profile" />
          </div>
          {!hideMenuButton && (
            <div>
              <IconButton
                color="primary"
                variant="text"
                onClick={handleToggleMenu}
                data-testid="navbar-mobile-toggle-menu"
              >
                <IconMenuToggle />
              </IconButton>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Navbar */}
      <div className="hidden items-center justify-between border-b border-gray-200 bg-white px-4 py-2 lg:flex lg:px-6 lg:py-5">
        <Link to="/dashboard" data-testid="navbar-desktop-dashboard-link">
          <IconLogoFlightDeck />
        </Link>
        <div className="flex items-center gap-6">
          <div className="hidden items-center gap-3 lg:flex">
            <SearchInputExternalResolver
              placeholder="Search..."
              value={desktopSearchTerm}
              onChange={setDesktopSearchTerm}
              options={desktopSearchOptions}
              optionsLoading={isDesktopSearchLoading}
              onSelectOption={handleDesktopSearchSelect}
              onDeselectOption={() => setDesktopSearchTerm('')}
              className="w-80"
              testIdentifier="navbar-desktop-search"
            />
            <UnifiedIconButton
              mode="ghost"
              onClick={() => navigate('/mail')}
              testIdentifier="navbar-desktop-mail"
            >
              <MailIcon size={32} />
            </UnifiedIconButton>
            <UnifiedIconButton
              mode="ghost"
              onClick={() => navigate('/calendar')}
              testIdentifier="header-calendar-button"
            >
              <CalendarIcon size={32} />
            </UnifiedIconButton>
            {hasMeetingsAccess ? (
              <span className="relative inline-flex" data-testid="header-meetings-wrapper">
                <UnifiedIconButton
                  mode="ghost"
                  onClick={() => navigate('/meetings')}
                  testIdentifier="header-meetings-button"
                  title="Meetings"
                  aria-current={isMeetingsPage ? 'page' : undefined}
                  className={isMeetingsPage ? 'text-primary' : undefined}
                >
                  <PresentationIcon
                    size={32}
                    className={isMeetingsPage ? 'text-primary' : undefined}
                  />
                </UnifiedIconButton>
                {meetingsNeedsReviewCount > 0 ? (
                  <div
                    data-testid="header-meetings-badge"
                    className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#106df6] px-1 text-2xs font-semibold text-white"
                  >
                    {meetingsNeedsReviewCount > 99
                      ? 99
                      : meetingsNeedsReviewCount}
                  </div>
                ) : null}
              </span>
            ) : null}
            <NotificationPanel />
            <UnifiedIconButton
              mode="ghost"
              onClick={() => navigate('/find-products')}
              testIdentifier="header-cart-button"
            >
              <ShoppingCartIcon size={32} />
            </UnifiedIconButton>
          </div>
          <div className="flex items-center gap-3">
            <UserProfileWidget testIdentifierPrefix="navbar-desktop-user-profile" />
          </div>
          {hasAiAssistantPermission && (
            <div className="hidden lg:block">
              <Button
                variant="primary"
                leftSlot={<IconStarBadge fill="white" size={20} />}
                onClick={() => navigate('/wingman')}
                className="border-0 bg-gradient-to-t from-[#6b7fea] to-[#8999ee] text-white hover:from-[#5a6fd9] hover:to-[#7888dd]"
                testIdentifier="navbar-desktop-ai-assistant"
              >
                Ask AI
              </Button>
            </div>
          )}
        </div>
      </div>

      <MobileSearchModal
        open={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </>
  );
};
export default Navbar;
